import React from "react";
import {useTranslation} from "react-i18next";
import ReportingInfoTooltip from "../../components/ReportingInfoTooltip.jsx";
import {useRenderCurrencyValue, useRenderNumber} from "../../api/utilities/useValueRenderers.js";

export function selectedCentreSpendAttributionStatus(attribution, selectedCentreFilter = false, requireAttribution = false) {
    if (attribution?.complete === false) {
        return "incomplete";
    }

    if ((selectedCentreFilter || requireAttribution) && attribution?.complete !== true) {
        return "unknown";
    }

    return null;
}

export function centreAttributionRowTooltipPosition(index, rowCount) {
    return rowCount > 0 && index >= rowCount - 2 ? "top" : "bottom";
}

export function CentreAttributionRowStatus({status, tooltipPosition = "bottom"}) {
    const {t} = useTranslation();
    if (status === null || status === undefined) {
        return null;
    }

    const unknown = status === "unknown";
    return <span className="ml-1 inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-medium text-amber-700 dark:text-amber-300">
        {t(unknown
            ? "reporting_lab.centre_spend_attribution.row_unknown_short"
            : "reporting_lab.centre_spend_attribution.channel_partial_short", {
            defaultValue: unknown ? "Neověřeno" : "Mezisoučet",
        })}
        <ReportingInfoTooltip size="md" position={tooltipPosition} ariaLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}>
            {t(unknown
                ? "reporting_lab.centre_spend_attribution.unknown_help"
                : "reporting_lab.centre_spend_attribution.row_partial_help", {
                defaultValue: unknown
                    ? "Odpověď neobsahuje údaje o úplnosti přiřazení ani o částkách. Hodnoty PNO a zisku podle středisek proto mohou být neúplné; neznámé náklady nelze přiřadit k žádnému středisku."
                    : "Část reklamních nákladů není přiřazená ke středisku.",
            })}
        </ReportingInfoTooltip>
    </span>;
}

function hasMoneyAmount(value) {
    const amount = value?.amount;
    const validAmount = typeof amount === "number"
        ? Number.isFinite(amount)
        : typeof amount === "string" && amount.trim() !== "" && Number.isFinite(Number(amount));

    return validAmount && typeof value.currency === "string" && value.currency.trim() !== "";
}

function hasCount(value) {
    const numericValue = typeof value === "number"
        ? value
        : typeof value === "string" && value.trim() !== "" ? Number(value) : Number.NaN;

    return Number.isInteger(numericValue) && numericValue >= 0;
}

export function centreSpendAttributionNotice(attribution, {
    selectedCentreFilter = false,
    requireAttribution = false,
    t,
    renderCurrencyValue,
    renderNumber,
}) {
    const status = selectedCentreSpendAttributionStatus(attribution, selectedCentreFilter, requireAttribution);
    if (!status) {
        return null;
    }

    if (status === "unknown") {
        return {
            status,
            visibleStatus: t("reporting_lab.centre_spend_attribution.unknown_notice_short", {
                defaultValue: "Přiřazení reklamních nákladů ke středisku nelze ověřit",
            }),
            detail: t("reporting_lab.centre_spend_attribution.unknown_help", {
                defaultValue: "Odpověď neobsahuje údaje o úplnosti přiřazení ani o částkách. Hodnoty PNO a zisku podle středisek proto mohou být neúplné; neznámé náklady nelze přiřadit k žádnému středisku.",
            }),
        };
    }

    const assignedKnown = hasMoneyAmount(attribution.assignedSpendMarketing);
    const unassignedKnown = hasMoneyAmount(attribution.unassignedSpendMarketing);
    const unassignedPositive = unassignedKnown && Number(attribution.unassignedSpendMarketing.amount) > 0;
    const assigned = assignedKnown ? renderCurrencyValue(attribution.assignedSpendMarketing, true) : null;
    const unassigned = unassignedKnown ? renderCurrencyValue(attribution.unassignedSpendMarketing, true) : null;
    let amounts = null;

    if (assignedKnown && unassignedKnown) {
        amounts = t("reporting_lab.centre_spend_attribution.amounts_short", {
            defaultValue: "Napříč středisky: přiřazeno {{assigned}}, nepřiřazeno {{unassigned}}",
            assigned,
            unassigned,
        });
    } else if (assignedKnown) {
        amounts = t("reporting_lab.centre_spend_attribution.assigned_amount_short", {
            defaultValue: "Napříč středisky přiřazeno: {{amount}}",
            amount: assigned,
        });
    } else if (unassignedKnown) {
        amounts = t("reporting_lab.centre_spend_attribution.unassigned_amount_short", {
            defaultValue: "Napříč středisky nepřiřazeno: {{amount}}",
            amount: unassigned,
        });
    }

    const countsKnown = hasCount(attribution.unmappedSourceCount) && hasCount(attribution.ambiguousSourceCount);
    const summary = assignedKnown && unassignedKnown && countsKnown
        ? t("reporting_lab.centre_spend_attribution.summary", {
            defaultValue: "Napříč všemi středisky: přiřazeno {{assigned}}, nepřiřazeno {{unassigned}}. Nerozlišené zdroje: {{unmapped}}; rozporná přiřazení: {{ambiguous}}.",
            assigned,
            unassigned,
            unmapped: renderNumber(attribution.unmappedSourceCount),
            ambiguous: renderNumber(attribution.ambiguousSourceCount),
        })
        : null;

    return {
        status,
        visibleStatus: t(unassignedPositive
            ? "reporting_lab.centre_spend_attribution.unassigned_partial_notice_short"
            : "reporting_lab.centre_spend_attribution.incomplete_notice_short", {
            defaultValue: unassignedPositive
                ? "Část známých reklamních nákladů není přiřazena ke středisku"
                : "Přiřazení reklamních nákladů ke středisku je neúplné",
        }),
        amounts,
        summary,
        scope: t("reporting_lab.centre_spend_attribution.scope", {
            defaultValue: "Referenční souhrn používá běžný reportový rozsah projektu pro stejné období a vybrané domény a ignoruje pouze filtr středisek. Zachovává běžná vyloučení známých středisek, včetně B2B; nezařazené náklady zůstávají, protože jejich středisko neznáme. Nejde o náklady vybraného střediska.",
        }),
        selectedCentreNote: selectedCentreFilter
            ? t("reporting_lab.centre_spend_attribution.selected", {
                defaultValue: "Výkon vybraných středisek ukazuje známý přiřazený mezisoučet. Nepřiřazené náklady nelze přiřadit k vybranému středisku, proto jsou jeho souhrnné PNO a zisk neúplné.",
            })
            : null,
    };
}

export default function CentreSpendAttributionNotice({attribution, selectedCentreFilter = false, requireAttribution = false, loading = false}) {
    const {t} = useTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const notice = centreSpendAttributionNotice(attribution, {selectedCentreFilter, requireAttribution, t, renderCurrencyValue, renderNumber});

    if (loading || !notice) {
        return null;
    }

    return <div role="status" className="app-surface col-span-12 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
        <span className="font-medium">{notice.visibleStatus}</span>
        {notice.amounts && <span>{notice.amounts}</span>}
        <ReportingInfoTooltip size="md" ariaLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}>
            {notice.detail && <p>{notice.detail}</p>}
            {notice.summary && <p>{notice.summary}</p>}
            {notice.scope && <p className="mt-2">{notice.scope}</p>}
            {notice.selectedCentreNote && <p className="mt-2">{notice.selectedCentreNote}</p>}
        </ReportingInfoTooltip>
    </div>;
}
